import { useState } from 'react';
import { validateSignUp } from '../lib/validation';
import { supabase } from '../lib/supabase';
import { useSectors } from '../hooks/useSectors';
import { Button } from './ui/button/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface AuthSignUpProps {
  onBackToSignIn: () => void;
}

export function AuthSignUp({ onBackToSignIn }: AuthSignUpProps) {
  const {
    sectors,
    loading: sectorsLoading,
    error: sectorsError,
  } = useSectors();

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sector, setSector] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [whatBuilding, setWhatBuilding] = useState('');
  const [jobRole, setJobRole] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSignUp = async () => {
    setError(null);

    const validationError = validateSignUp({
      firstName,
      email,
      password,
      confirmPassword,
      sector,
      termsAccepted,
      privacyAccepted,
    });
    if (validationError) return setError(validationError);

    const acceptedAt = new Date().toISOString();

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp(
      {
        email,
        password,
        options: {
          data: {
            first_name: firstName.trim(),
            sector,
            company_name: companyName.trim() || null,
            what_building: whatBuilding.trim() || null,
            role: jobRole.trim() || null,
            how_heard: howHeard.trim() || null,
            terms_accepted_at: acceptedAt,
            privacy_accepted_at: acceptedAt,
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      },
    );

    if (signUpError) return setError(signUpError.message);
    if (!signUpData.user || (signUpData.user.identities?.length ?? 1) === 0) {
      return setError(
        'An account with this email already exists. Please sign in instead.',
      );
    }
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="space-y-4">
        <div className="space-y-2 text-center">
          <p className="font-semibold">Check your email</p>
          <p className="text-sm text-muted-foreground">
            We've sent a confirmation link to {email}. Click the link to
            activate your account and start your 7-day free trial.
          </p>
        </div>
        <Button variant="ghost" onClick={onBackToSignIn} className="w-full">
          Back to sign in
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <p className="text-sm font-medium">First name *</p>
          <Input
            type="text"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full bg-white"
          />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">Email *</p>
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-white"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <p className="text-sm font-medium">Password *</p>
          <Input
            type="password"
            placeholder="Min. 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-white"
          />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium">Confirm password *</p>
          <Input
            type="password"
            placeholder="Re-enter password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full bg-white"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        At least 8 characters, one uppercase, one lowercase, one number
      </p>
      <div className="space-y-1">
        <p className="text-sm font-medium">Sector *</p>
        <Select
          value={sector}
          onValueChange={setSector}
          disabled={sectorsLoading || sectorsError}
        >
          <SelectTrigger className="w-full bg-white">
            <SelectValue
              placeholder={
                sectorsError
                  ? 'Failed to load sectors — please refresh'
                  : 'What sector or industry are you in?'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {sectors.map((s) => (
              <SelectItem key={s.id} value={s.slug}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <hr className="border-border" />

      <div className="space-y-2">
        <p className="text-sm font-semibold text-foreground">
          Tell us a bit more
          <span className="font-normal text-muted-foreground"> (optional)</span>
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <p className="text-sm font-medium">Company name</p>
            <Input
              type="text"
              placeholder="Company name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="bg-white"
            />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">Your role</p>
            <Input
              type="text"
              placeholder="e.g. Product manager, founder"
              value={jobRole}
              onChange={(e) => setJobRole(e.target.value)}
              className="bg-white"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <p className="text-sm font-medium">What are you building?</p>
            <Input
              type="text"
              placeholder="e.g. a customer support agent"
              value={whatBuilding}
              onChange={(e) => setWhatBuilding(e.target.value)}
              className="bg-white"
            />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium">How did you hear about us?</p>
            <Input
              type="text"
              placeholder="LinkedIn, a colleague"
              value={howHeard}
              onChange={(e) => setHowHeard(e.target.value)}
              className="bg-white"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-4 pt-1">
        <label className="flex cursor-pointer items-center gap-1.5">
          <Checkbox
            checked={termsAccepted}
            onCheckedChange={(v) => setTermsAccepted(v === true)}
          />
          <span className="text-xs">
            I agree to the{' '}
            <a
              href="https://www.prompt-safe.com/terms"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Terms
            </a>
          </span>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5">
          <Checkbox
            checked={privacyAccepted}
            onCheckedChange={(v) => setPrivacyAccepted(v === true)}
          />
          <span className="text-xs">
            I agree to the{' '}
            <a
              href="https://www.prompt-safe.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Privacy policy
            </a>
          </span>
        </label>
      </div>

      {error && <div className="text-center text-sm text-red-600">{error}</div>}

      <Button
        onClick={handleSignUp}
        className="hover:bg-prompt-safecomoutrageous-orange/90 w-full bg-prompt-safecomoutrageous-orange text-white"
      >
        Create account
      </Button>
      <Button variant="ghost" onClick={onBackToSignIn} className="w-full">
        Already have an account? Sign in
      </Button>
    </div>
  );
}
